// The frame's side of the postMessage channel with embed.js.

import { unwrap, wrap, type PageInfo, type ToFrame, type ToParent } from "../../shared/protocol.ts";

const params = new URLSearchParams(location.search);
/** From the frame URL; the kiosk page sets it once it knows it (setApiKey). */
export let API_KEY = params.get("key") ?? "";
export function setApiKey(key: string) {
  API_KEY = key;
}
export const framed = window.parent !== window;

/**
 * Origin of the page we are embedded in. ancestorOrigins cannot be faked by
 * the page (Chromium, Safari); Firefox falls back to the referrer, then to the
 * host parameter embed.js put in our URL.
 */
export const parentOrigin: string | null = (() => {
  if (!framed) return null;
  const ancestor = location.ancestorOrigins?.[0];
  if (ancestor) return ancestor;
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch {
    // fall through
  }
  const host = params.get("host");
  try {
    return host ? new URL(host).origin : null;
  } catch {
    return null;
  }
})();

export function toParent(message: ToParent) {
  if (!framed || !parentOrigin) return;
  window.parent.postMessage(wrap(message), parentOrigin);
}

type Listener = (message: ToFrame) => void;
const listeners = new Set<Listener>();

window.addEventListener("message", (event) => {
  if (event.source !== window.parent || event.origin !== parentOrigin) return;
  const message = unwrap<ToFrame>(event.data);
  if (message) for (const listener of listeners) listener(message);
});

export function onParent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let nextId = 1;

/** Where the visitor is now (the page may have changed since the frame loaded). */
export function requestContext(timeoutMs = 400): Promise<{ url: string; ref: string | null; page: PageInfo | null } | null> {
  if (!framed) return Promise.resolve(null);
  const id = nextId++;
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      off();
      resolve(null);
    }, timeoutMs);
    const off = onParent((message) => {
      if (message.type !== "context" || message.id !== id) return;
      window.clearTimeout(timer);
      off();
      resolve({ url: message.url, ref: message.ref, page: message.page ?? null });
    });
    toParent({ type: "context-request", id });
  });
}
